/**
 * Single source of truth for turning raw API errors into actionable Russian
 * copy. Without this every call-site invented its own message; users would
 * see "Network error", "Failed to fetch", "fetch failed" depending on which
 * code-path threw — none of which suggested what to do.
 *
 * Pattern: pass the error in (could be Error, string, anything), get back
 * `{ title, hint? }`. Callers feed it to sonner's `toast.error(title,
 * { description: hint })`.
 *
 * The mapping is keyword-based on either `err.body.error` (our standardized
 * code) or `err.message`. We keep keys lowercase + check substrings so an
 * upstream rename of "blocked" → "blocked_by_recipient" still routes to
 * the same friendly copy without code changes.
 */

import i18n from '@/i18n';

export interface HumanError {
  title: string;
  hint?: string;
  /** Optional retry-after in seconds if the server returned it. */
  retryAfter?: number;
}

interface ErrorWithBody {
  message?: string;
  status?: number;
  body?: { error?: string; message?: string; retryAfter?: number; slowmodeS?: number; auto?: boolean };
}

// Keyword → i18n-key mapping. Titles/hints are resolved through `i18n.t`
// at call time (see humanizeApiError) so they follow the active language.
const TABLE: Array<{ match: RegExp; titleKey: string; hintKey?: string }> = [
  // ---------- auth / session ----------
  {
    match: /(unauthorized|no_session|session_revoked|csrf_origin_mismatch)/i,
    titleKey: 'ui.err.sessionExpired.title',
    hintKey: 'ui.err.sessionExpired.hint',
  },
  // ---------- send rate / slowmode / anti-spam ----------
  {
    match: /duplicate_message/i,
    titleKey: 'ui.err.duplicate.title',
    hintKey: 'ui.err.duplicate.hint',
  },
  {
    match: /new_member_wait/i,
    titleKey: 'ui.err.newMemberWait.title',
    hintKey: 'ui.err.newMemberWait.hint',
  },
  {
    match: /\bslowmode\b/i,
    titleKey: 'ui.err.slowmode.title',
    hintKey: 'ui.err.slowmode.hint',
  },
  {
    // Not a bare `too_many`: `too_many_items` (a full task list) is a limit
    // on the thing, not on how fast you go — it read as «slow down».
    match: /rate.?limit|too.?many.?requests|\b429\b/i,
    titleKey: 'ui.err.rateLimit.title',
    hintKey: 'ui.err.rateLimit.hint',
  },
  // ---------- blocks ----------
  {
    match: /blocked_by_recipient/i,
    titleKey: 'ui.err.blockedByRecipient.title',
    hintKey: 'ui.err.blockedByRecipient.hint',
  },
  {
    match: /\bblocked\b/i,
    titleKey: 'ui.err.blocked.title',
    hintKey: 'ui.err.blocked.hint',
  },
  {
    match: /dm_friends_only/i,
    titleKey: 'ui.err.dmFriendsOnly.title',
    hintKey: 'ui.err.dmFriendsOnly.hint',
  },
  {
    match: /dm_disabled/i,
    titleKey: 'ui.err.dmDisabled.title',
    hintKey: 'ui.err.dmDisabled.hint',
  },
  // ---------- uploads ----------
  {
    match: /file_too_large/i,
    titleKey: 'ui.err.fileTooLarge.title',
    hintKey: 'ui.err.fileTooLarge.hint',
  },
  {
    match: /mime_not_allowed/i,
    titleKey: 'ui.err.mimeNotAllowed.title',
    hintKey: 'ui.err.mimeNotAllowed.hint',
  },
  {
    match: /s3_not_configured/i,
    titleKey: 'ui.err.s3NotConfigured.title',
    hintKey: 'ui.err.s3NotConfigured.hint',
  },
  {
    match: /s3_put_failed|s3_put_network/i,
    titleKey: 'ui.err.s3PutFailed.title',
    hintKey: 'ui.err.s3PutFailed.hint',
  },
  // ---------- messages ----------
  {
    match: /reaction_limit_reached/i,
    titleKey: 'ui.err.reactionLimit.title',
    hintKey: 'ui.err.reactionLimit.hint',
  },
  // ---------- roles ----------
  { match: /role_not_found/i, titleKey: 'ui.err.roleGone.title', hintKey: 'ui.err.roleGone.hint' },
  { match: /role_limit_reached/i, titleKey: 'ui.err.roleLimit.title', hintKey: 'ui.err.roleLimit.hint' },
  { match: /permission_escalation_forbidden/i, titleKey: 'ui.err.permissionEscalation.title' },
  {
    match: /role_hierarchy_forbidden/i,
    titleKey: 'ui.err.roleHierarchy.title',
    hintKey: 'ui.err.roleHierarchy.hint',
  },
  // ---------- voice on air / moderation ----------
  { match: /broadcast_on_air/i, titleKey: 'ui.err.broadcastOnAir.title', hintKey: 'ui.err.broadcastOnAir.hint' },
  {
    match: /broadcast_consent_missing/i,
    titleKey: 'ui.err.broadcastConsentMissing.title',
    hintKey: 'ui.err.broadcastConsentMissing.hint',
  },
  { match: /timeout_in_past/i, titleKey: 'ui.err.timeoutInPast.title' },
  { match: /timeout_too_long/i, titleKey: 'ui.err.timeoutTooLong.title' },
  // ---------- invites / guild ----------
  {
    match: /invite_expired|invite expired/i,
    titleKey: 'ui.err.inviteExpired.title',
    hintKey: 'ui.err.inviteExpired.hint',
  },
  {
    match: /invite_exhausted|max_uses/i,
    titleKey: 'ui.err.inviteExhausted.title',
    hintKey: 'ui.err.inviteExhausted.hint',
  },
  {
    // Invite codes only: a bare `not_found|404` here turned every missing
    // message, channel or role into «Приглашение не найдено». Other 404s fall
    // through to the status default below.
    match: /invite_invalid|invite_not_found/i,
    titleKey: 'ui.err.inviteInvalid.title',
    hintKey: 'ui.err.inviteInvalid.hint',
  },
  {
    match: /already.?member|already_in_guild/i,
    titleKey: 'ui.err.alreadyMember.title',
  },
  // ---------- voice ----------
  {
    match: /no_voice_perm|voice_forbidden/i,
    titleKey: 'ui.err.noVoicePerm.title',
    hintKey: 'ui.err.noVoicePerm.hint',
  },
  {
    match: /voice_full|user_limit/i,
    titleKey: 'ui.err.voiceFull.title',
    hintKey: 'ui.err.voiceFull.hint',
  },
  {
    match: /timeout_active|timed_out/i,
    titleKey: 'ui.err.timeout.title',
    hintKey: 'ui.err.timeout.hint',
  },
  // ---------- network ----------
  {
    match: /failed to fetch|network|fetch failed|networkerror/i,
    titleKey: 'ui.err.network.title',
    hintKey: 'ui.err.network.hint',
  },
  {
    match: /bad_response/i,
    titleKey: 'ui.err.badResponse.title',
    hintKey: 'ui.err.badResponse.hint',
  },
];

const STATUS_DEFAULTS: Record<number, { titleKey: string; hintKey?: string }> = {
  401: { titleKey: 'ui.err.status401.title', hintKey: 'ui.err.status401.hint' },
  403: { titleKey: 'ui.err.status403.title' },
  404: { titleKey: 'ui.err.status404.title' },
  413: { titleKey: 'ui.err.status413.title' },
  429: { titleKey: 'ui.err.rateLimit.title', hintKey: 'ui.err.rateLimit.hint' },
  500: { titleKey: 'ui.err.status500.title', hintKey: 'ui.err.status500.hint' },
  502: { titleKey: 'ui.err.status502.title', hintKey: 'ui.err.status502.hint' },
  503: { titleKey: 'ui.err.status503.title' },
};

/** Convert any error-shaped value into a `{ title, hint? }` for toasting. */
export function humanizeApiError(err: unknown, fallback?: string): HumanError {
  const e = err as ErrorWithBody | undefined;
  const raw =
    (typeof e?.body?.error === 'string' && e.body.error) ||
    (typeof e?.body?.message === 'string' && e.body.message) ||
    (typeof e?.message === 'string' && e.message) ||
    (typeof err === 'string' ? err : '') ||
    '';

  for (const rule of TABLE) {
    if (rule.match.test(raw)) {
      const out: HumanError = { title: i18n.t(rule.titleKey) };
      if (rule.hintKey) out.hint = i18n.t(rule.hintKey);
      // Slowmode + 429 may include a server-provided retryAfter (seconds).
      if (typeof e?.body?.retryAfter === 'number') {
        out.retryAfter = e.body.retryAfter;
        // Personalize the hint with the exact wait.
        const seconds = e.body.retryAfter;
        if (/slowmode/i.test(raw) && e.body.auto) {
          out.title = i18n.t('ui.err.slowmode.autoTitle');
          out.hint = i18n.t('ui.err.slowmode.autoHint', { seconds });
        } else if (/slowmode/i.test(raw)) {
          out.hint = i18n.t('ui.err.slowmode.hintTimed', { seconds });
        } else if (/duplicate_message/i.test(raw)) {
          out.hint = i18n.t('ui.err.duplicate.hintTimed', { seconds });
        } else if (/new_member_wait/i.test(raw)) {
          out.hint = i18n.t('ui.err.newMemberWait.hintTimed', { minutes: Math.ceil(seconds / 60) });
        }
      }
      return out;
    }
  }

  if (typeof e?.status === 'number' && STATUS_DEFAULTS[e.status]) {
    const def = STATUS_DEFAULTS[e.status]!;
    const out: HumanError = { title: i18n.t(def.titleKey) };
    if (def.hintKey) out.hint = i18n.t(def.hintKey);
    return out;
  }

  return { title: fallback ?? i18n.t('ui.err.fallback') };
}

/**
 * Convenience wrapper for toast call-sites: `toast.error(...errToToast(err))`.
 * Returns the tuple sonner expects.
 */
export function errToToast(err: unknown, fallback?: string): [string, { description?: string }] {
  const h = humanizeApiError(err, fallback);
  return [h.title, h.hint ? { description: h.hint } : {}];
}
